import { FC, useCallback, useState } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import AddIcon from '@mui/icons-material/Add';
import IndicatorForm from './IndicatorForm';

export interface ScoreValues {
  score: number;
  label: string;
  gateway_criteria: string[];
  differentiation: string;
}

export interface PatternValues {
  pattern: string;
  example: string;
}

export interface NegativeEvidenceValues {
  red_flags: PatternValues[];
  score_reducing_patterns: PatternValues[];
}

export interface IndicatorValues {
  indicator: string;
  description: string;
  reason: string;
  probe: string;
  trigger: string;
  scores: ScoreValues[];
  negative_evidence: NegativeEvidenceValues;
}

export const DEFAULT_SCORE_LABELS = [
  'Failed Response',
  'Weak',
  'Adequate',
  'Strong',
  'Internalised Mastery',
] as const;

export const emptyIndicator = (): IndicatorValues => ({
  indicator: '',
  description: '',
  reason: '',
  probe: '',
  trigger: '',
  scores: DEFAULT_SCORE_LABELS.map((label, i) => ({
    score: i + 1,
    label,
    gateway_criteria: [],
    differentiation: '',
  })),
  negative_evidence: {
    red_flags: [],
    score_reducing_patterns: [],
  },
});

interface EditIndicatorsDialogProps {
  open: boolean;
  onClose: () => void;
  softSkillName: string;
  initialIndicators: IndicatorValues[];
  onSave: (indicators: IndicatorValues[]) => void;
  loading?: boolean;
}

const EditIndicatorsDialog: FC<EditIndicatorsDialogProps> = ({
  open,
  onClose,
  softSkillName,
  initialIndicators,
  onSave,
  loading,
}) => {
  const [indicators, setIndicators] = useState<IndicatorValues[]>(
    initialIndicators.length > 0 ? initialIndicators : [],
  );

  const handleIndicatorChange = useCallback(
    (indicatorIndex: number, updated: IndicatorValues) => {
      setIndicators(prev =>
        prev.map((ind, i) => (i === indicatorIndex ? updated : ind)),
      );
    },
    [],
  );

  const handleRemoveIndicator = useCallback((indicatorIndex: number) => {
    setIndicators(prev => prev.filter((_, i) => i !== indicatorIndex));
  }, []);

  const handleAddIndicator = useCallback(() => {
    setIndicators(prev => [...prev, emptyIndicator()]);
  }, []);

  const handleSave = () => {
    onSave(indicators);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>Edit Indicators — {softSkillName}</DialogTitle>
      <DialogContent>
        {indicators.map((ind, index) => (
          <IndicatorForm
            key={index}
            index={index}
            values={ind}
            onChange={updated => handleIndicatorChange(index, updated)}
            onRemove={() => handleRemoveIndicator(index)}
          />
        ))}
        <Box sx={{ mt: 1 }}>
          <Button
            startIcon={<AddIcon />}
            onClick={handleAddIndicator}
            size="small"
          >
            Add Indicator
          </Button>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>
          Cancel
        </Button>
        <Button onClick={handleSave} variant="contained" disabled={loading}>
          {loading ? 'Saving...' : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EditIndicatorsDialog;
